import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ship, MapPin, CalendarDays, Music2 } from "lucide-react";
import { HubHeader, HubFooter } from "../components/HubChrome";
import { useSeo } from "../lib/seo";
import { seo } from "../seo/schema";

const CRUISES = [
  { to: "/escape", tag: "Next Sailing • Dec 13–20, 2026", name: "The Escape", ship: "Norwegian Breakaway", port: "Round-trip New Orleans", route: "Cozumel • Roatán • Harvest Caye • Costa Maya", img: "/assets/escape/hero-breakaway.jpg", color: "#6EE6E6" },
  { to: "/europe", tag: "Jun 17–26, 2027", name: "The Europe Experience", ship: "Celebrity Equinox", port: "Round-trip Barcelona", route: "Seville • Lisbon (SuncéBeat) • Porto • Tangier", img: "/assets/europe/hero-equinox.jpg", color: "#84FE2A" },
  { to: "/getaway", tag: "Dec 4–11, 2027", name: "The Getaway", ship: "Norwegian Prima", port: "Round-trip San Juan", route: "Tortola • St. Kitts • Barbados • St. Lucia • St. Thomas • St. Maarten", img: "/prima.jpg", color: "#F2C04C" },
];

const CruiseCard = ({ c, i }) => (
  <Link
    to={c.to}
    data-testid={`landing-cruise-card-${c.to.slice(1)}`}
    className="group relative flex flex-col bg-[#111111] border border-white/10 rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 animate-[fadeIn_0.6s_ease-out_both]"
    style={{ animationDelay: `${i * 120}ms` }}
  >
    <div className="relative h-52 overflow-hidden">
      <img src={c.img} alt={`${c.ship} — House Heads At Sea ${c.name}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#111111] to-transparent" />
      <span className="absolute top-4 left-4 font-cond uppercase text-[10px] tracking-widest rounded-full px-3 py-1 text-black" style={{ background: c.color }}>{c.tag}</span>
    </div>
    <div className="p-6 flex flex-col flex-1">
      <h2 className="font-display uppercase text-white text-3xl" style={{ color: c.color }}>{c.name}</h2>
      <p className="mt-2 flex items-center gap-2 text-white/75 text-sm"><Ship size={15} /> {c.ship}</p>
      <p className="mt-1 flex items-center gap-2 text-white/75 text-sm"><MapPin size={15} /> {c.port}</p>
      <p className="mt-3 text-white/50 text-sm flex-1">{c.route}</p>
      <span className="mt-5 inline-flex items-center gap-2 font-cond uppercase tracking-widest text-sm text-white group-hover:gap-3 transition-all">
        Explore {c.name} <ArrowRight size={16} />
      </span>
    </div>
  </Link>
);

export default function Landing() {
  useSeo("/");
  const page = seo.pages["/"];
  const featured = CRUISES[0];
  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white" data-testid="landing-page">
      <HubHeader />
      <section className="relative overflow-hidden">
        <img src={featured.img} alt="Norwegian Breakaway — House Heads At Sea The Escape 2026" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 py-24 sm:py-32">
          <p className="font-cond uppercase tracking-[0.4em] text-[#E50914] text-sm mb-4">The House Music Cruise Experience</p>
          <h1 data-testid="landing-h1" className="font-display uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            House Heads <span className="text-[#E50914]">At Sea</span>
          </h1>
          <p className="mt-6 text-white/75 text-base sm:text-lg max-w-2xl leading-relaxed">{page.intro}</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/escape" data-testid="landing-featured-cta" className="inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#c20710] text-white font-cond uppercase tracking-widest rounded-full px-8 py-4 transition-colors">
              Sail The Escape 2026 <ArrowRight size={18} />
            </Link>
            <span className="flex items-center gap-2 text-white/60 text-sm"><CalendarDays size={16} className="text-[#6EE6E6]" /> Next sailing: Dec 13, 2026 from New Orleans</span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-20" aria-labelledby="voyages">
        <p id="voyages" className="font-cond uppercase tracking-[0.4em] text-white/50 text-sm mb-8">Choose Your Voyage</p>
        <div className="grid md:grid-cols-3 gap-6">
          {CRUISES.map((c, i) => <CruiseCard key={c.to} c={c} i={i} />)}
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-5 py-20 grid md:grid-cols-2 gap-12">
          {page.sections.map((s) => (
            <div key={s.h2}>
              <h2 className="font-cond font-bold uppercase tracking-widest text-white text-xl flex items-center gap-2"><Music2 size={18} className="text-[#E50914]" /> {s.h2}</h2>
              <ul className="mt-5 space-y-3">
                {s.items.map((it) => <li key={it} className="text-white/65 text-sm leading-relaxed border-l-2 border-[#E50914]/60 pl-4">{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>
      <HubFooter />
    </div>
  );
}
